import type { SSEEvent, CollabUpdateEvent, NotificationEvent } from '../types';

type EventCallback = (event: SSEEvent) => void;

/**
 * SSE (Server-Sent Events) connection manager
 * Handles reconnection logic and event parsing
 */
class SSEManager {
    private eventSource: EventSource | null = null;
    private url: string;
    private callbacks: Set<EventCallback> = new Set();
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectTimeout: number | null = null;
    private isConnected = false;

    constructor(url: string) {
        this.url = url;
    }

    connect(): void {
        if (this.eventSource) {
            this.disconnect();
        }

        try {
            this.eventSource = new EventSource(this.url);

            this.eventSource.onopen = () => {
                console.log(`SSE connected: ${this.url}`);
                this.isConnected = true;
                this.reconnectAttempts = 0;
            };

            this.eventSource.onmessage = (event) => {
                try {
                    console.log('[SSE] Raw event received:', event.data);
                    const data = JSON.parse(event.data) as SSEEvent;
                    console.log('[SSE] Parsed event:', data);
                    this.callbacks.forEach(cb => cb(data));
                } catch (e) {
                    console.error('Failed to parse SSE event:', e);
                }
            };

            this.eventSource.onerror = () => {
                console.log(`SSE error/closed: ${this.url}`);
                this.isConnected = false;
                this.handleReconnect();
            };
        } catch (e) {
            console.error('Failed to create EventSource:', e);
            this.handleReconnect();
        }
    }

    private handleReconnect(): void {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.log('Max SSE reconnect attempts reached');
            return;
        }

        const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
        this.reconnectAttempts++;

        console.log(`SSE reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

        this.reconnectTimeout = window.setTimeout(() => {
            this.connect();
        }, delay);
    }

    disconnect(): void {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }

        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }

        this.isConnected = false;
        this.reconnectAttempts = 0;
    }

    subscribe(callback: EventCallback): () => void {
        this.callbacks.add(callback);
        return () => this.callbacks.delete(callback);
    }

    get connected(): boolean {
        return this.isConnected;
    }
}

// ============ User Events (notifications, achievements) ============

let userEventsManager: SSEManager | null = null;

export function connectUserEvents(onEvent: (event: NotificationEvent) => void): () => void {
    if (!userEventsManager) {
        userEventsManager = new SSEManager('/api/events/user');
    }

    const unsubscribe = userEventsManager.subscribe((event) => {
        console.log('[SSE Manager] Event received, type:', event.type);
        if (event.type !== 'ping' && event.type !== 'connected') {
            console.log('[SSE Manager] Forwarding event to handler:', event);
            onEvent(event as NotificationEvent);
        }
    });

    userEventsManager.connect();

    return () => {
        unsubscribe();
        // Don't disconnect - other components might be listening
    };
}

export function disconnectUserEvents(): void {
    if (userEventsManager) {
        userEventsManager.disconnect();
        userEventsManager = null;
    }
}

// ============ Collab Events (game state updates) ============

let collabEventsManager: SSEManager | null = null;

export function connectCollabEvents(
    sessionId: string,
    onUpdate: (event: CollabUpdateEvent) => void
): () => void {
    // Disconnect existing connection if session changed
    if (collabEventsManager) {
        collabEventsManager.disconnect();
    }

    collabEventsManager = new SSEManager(`/api/collab/stream?session_id=${sessionId}`);

    const unsubscribe = collabEventsManager.subscribe((event) => {
        // Backend publishes 'team_update' for collab game state changes
        if (event.type === 'collab_update' || event.type === 'team_update') {
            onUpdate(event as CollabUpdateEvent);
        }
    });

    collabEventsManager.connect();

    return () => {
        unsubscribe();
        collabEventsManager?.disconnect();
        collabEventsManager = null;
    };
}

// ============ Blitz Events (standings, phase changes) ============

let blitzEventsManager: SSEManager | null = null;

export interface BlitzEvent extends SSEEvent {
    type: 'blitz_update' | 'phase_change' | 'standings_update';
    payload: {
        phase?: string;
        time_remaining?: number;
        standings?: Array<{
            username: string;
            position: number;
            bestRank: number;
        }>;
    };
}

export function connectBlitzEvents(onEvent: (event: BlitzEvent) => void): () => void {
    if (!blitzEventsManager) {
        blitzEventsManager = new SSEManager('/api/blitz/stream');
    }

    const unsubscribe = blitzEventsManager.subscribe((event) => {
        onEvent(event as BlitzEvent);
    });

    blitzEventsManager.connect();

    return () => {
        unsubscribe();
    };
}

export function disconnectBlitzEvents(): void {
    if (blitzEventsManager) {
        blitzEventsManager.disconnect();
        blitzEventsManager = null;
    }
}
